import React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { FastField, Field, FieldProps, useFormikContext } from 'formik';
import { uniqueId } from 'lodash';
import { Popover, Select } from '@brighterion/br-ui';
import { shouldShowAddToList, shouldShowReviewConclusions, shouldShowSetFlags, shouldShowPanList } from 'Selectors';
import { configService } from 'Services';
import { getFormattedParameterTypeDisplayName } from '@brighterion/utils/parameters';
import { LIST_TYPES, RULE_ACTION_TYPE, SELECT_FLAG_PLACEHOLDER_TEXT, SELECT_PLACEHOLDER_TEXT } from 'Common/enums';
import { FormikInputField } from 'Components/FormikComponents';

import { ParameterType } from 'Common/parameterEnums';
import styles from './Conclusion.css';

type Option = {
    disabled?: boolean;
    label: string;
    value: string;
    visible?: boolean;
};

type Options = Option[];

export type AddToListData = Options;

type Props = {
    parameterData: UseGetParametersResponse | undefined;
    conclusionFlagListData?: ConclusionListApiGetResponse[];
    index: number;
    listData?: ListManagementApiList[];
    readOnly: boolean;
};

const Conclusion = ({ parameterData, conclusionFlagListData, index, listData, readOnly }: Props) => {
    const { t } = useTranslation();
    const { values, isSubmitting, handleBlur, handleChange } = useFormikContext<NewRuleFormSchema>();

    /** @note Update Action IDs after creating or removing an Action */
    React.useEffect(() => {
        values.actions[index].actionId = index;
    }, [index, values.actions]);

    const primaryTypes = [
        RULE_ACTION_TYPE.ACCEPT,
        RULE_ACTION_TYPE.REJECT,
        RULE_ACTION_TYPE.REVIEW,
        RULE_ACTION_TYPE.UPDATE_SCORE_BY
    ];

    const showReviewOption: Options = shouldShowReviewConclusions()
        ? [{ value: RULE_ACTION_TYPE.REVIEW, label: t('Review') }]
        : [];

    const primaryOptions: Options = [
        { value: RULE_ACTION_TYPE.ACCEPT, label: t('Accept') },
        { value: RULE_ACTION_TYPE.REJECT, label: t('Reject') },
        ...showReviewOption,
        { value: RULE_ACTION_TYPE.UPDATE_SCORE_BY, label: t('Update score by') }
    ];

    const setFlags =
        (shouldShowSetFlags() &&
            conclusionFlagListData?.length && [
                {
                    disabled: false,
                    label: t('Set Flags'),
                    value: RULE_ACTION_TYPE.SET_FLAGS,
                    visible: shouldShowSetFlags()
                }
            ]) ||
        [];

    const addToList =
        (shouldShowAddToList() && [
            {
                disabled: false,
                label: t('Add Entry to List'),
                value: RULE_ACTION_TYPE.ADD_TO_LIST,
                visible: shouldShowAddToList()
            }
        ]) ||
        [];

    const secondaryOptions: Options = [...setFlags, ...addToList];

    return (
        <div
            className={clsx([
                styles.conclusionStatementTextBar,
                values.actions[index].operator === RULE_ACTION_TYPE.UPDATE_SCORE_BY && styles.updateScoreBy,
                values.actions[index].operator === RULE_ACTION_TYPE.SET_FLAGS && styles.setFlags
            ])}
        >
            <div className={styles.conclusionColumn}>
                <span className={styles.conclusionLabel}>{index === 0 ? t('THEN') : t('AND')}</span>
            </div>

            <div className={styles.conclusionColumn}>
                {/** Target field operator within Action  */}
                <FastField name={`actions.${index}.operator`}>
                    {({ field, form: { setFieldValue } }: FieldProps) => {
                        if (primaryTypes.includes(field.value) || index === 0) {
                            return (
                                <Select
                                    size='sm'
                                    placeholder={`- ${t(SELECT_PLACEHOLDER_TEXT)} -`}
                                    handleClickCallback={(value) => {
                                        /**
                                         * Remove values from Action
                                         * @note Please, don't get confused by Formik field.value.
                                         */
                                        setFieldValue(field.name, value.toString());
                                        /**
                                         * Selecting another primary Operator type won't reset
                                         * `actions.${index}.values` like it does for Secondary types.
                                         * Yup can't validate the field if input type is string or number.
                                         *
                                         * @note On submit the payload will be modified.
                                         */
                                    }}
                                    selectedItems={field.value ? [field.value] : []}
                                    disabled={readOnly || isSubmitting}
                                    aria-disabled={readOnly}
                                >
                                    {primaryOptions.map(({ value, label }) => {
                                        return (
                                            <option key={uniqueId(value)} value={value}>
                                                {label}
                                            </option>
                                        );
                                    })}
                                </Select>
                            );
                        }
                        return (
                            <Select
                                size='sm'
                                placeholder={`- ${t(SELECT_PLACEHOLDER_TEXT)} -`}
                                handleClickCallback={(value) => {
                                    /**
                                     * Remove values from Action
                                     * @note Please, don't get confused by Formik field.value.
                                     */
                                    setFieldValue(`actions.${index}.values`, '');
                                    setFieldValue(field.name, value.toString());
                                }}
                                selectedItems={field.value ? [field.value] : []}
                                disabled={readOnly || isSubmitting}
                                aria-disabled={readOnly}
                            >
                                {secondaryOptions
                                    .filter((option) => option.visible)
                                    .map(({ value, label, disabled }) => {
                                        return (
                                            <option key={uniqueId(value)} value={value} disabled={disabled}>
                                                {label}
                                            </option>
                                        );
                                    })}
                            </Select>
                        );
                    }}
                </FastField>
            </div>

            {/** Target field values within Action if UPDATE_SCORE_BY is selected  */}
            {values.actions[index].operator === RULE_ACTION_TYPE.UPDATE_SCORE_BY && (
                <div className={styles.conclusionColumn}>
                    <FormikInputField
                        name={`actions[${index}].values`}
                        type='number'
                        min={configService.constants.minGlobalScore}
                        max={configService.constants.maxGlobalScore}
                        step={1}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        className={styles.conclusionValueInput}
                        disabled={isSubmitting}
                    />
                </div>
            )}

            {/** Target field values within Action if SET_FLAGS is selected */}
            {conclusionFlagListData && values.actions[index].operator === RULE_ACTION_TYPE.SET_FLAGS && (
                <div className={styles.conclusionColumn}>
                    <FastField name={`actions[${index}].values`}>
                        {({ field, form: { setFieldValue, setFieldTouched }, meta }: FieldProps) => {
                            /** After deployment or creating an override Set Flags values will be read-only */
                            if (readOnly) {
                                const filterSelectedValues = conclusionFlagListData.filter(({ name }) =>
                                    field.value.includes(name)
                                );
                                const fieldValues = Array.from(filterSelectedValues, ({ description }) => description);
                                return <span className={styles.readOnly}>{fieldValues.join(', ')}</span>;
                            }

                            return (
                                <Select
                                    name={field.name}
                                    size='sm'
                                    handleClickCallback={(value) => {
                                        setFieldValue(field.name, value);
                                        setFieldTouched(field.name, true);
                                    }}
                                    placeholder={`- ${t(SELECT_FLAG_PLACEHOLDER_TEXT)} -`}
                                    type='nonlistselector'
                                    selectedItems={field.value ? field.value : []}
                                    disabled={isSubmitting}
                                    error={!!(meta.error && meta.touched)}
                                    errorMsg={meta.error?.toString()}
                                >
                                    {conclusionFlagListData.map(({ name, description }) => {
                                        return (
                                            <option key={name} value={name}>
                                                {description}
                                            </option>
                                        );
                                    })}
                                </Select>
                            );
                        }}
                    </FastField>
                </div>
            )}

            {/** Target field values within Action if ADD_TO_LIST is selected  */}
            {parameterData && values.actions[index].operator === RULE_ACTION_TYPE.ADD_TO_LIST && (
                <>
                    <div className={styles.conclusionColumn}>
                        <FastField name={`actions[${index}].values.fieldName`}>
                            {({ field, form: { setFieldValue }, meta }: FieldProps) => {
                                return (
                                    <Select
                                        placeholder={`- ${t('Select Parameter')} -`}
                                        size='sm'
                                        type='search'
                                        searchInputPlaceholder='Search'
                                        handleClickCallback={(value) => {
                                            setFieldValue(`actions.${index}.values.listIds`, []);
                                            setFieldValue(field.name, value.toString());
                                        }}
                                        selectedItems={field.value ? [field.value] : []}
                                        disabled={readOnly || isSubmitting}
                                        aria-disabled={readOnly}
                                        error={!!(meta.error && meta.touched)}
                                        errorMsg={meta.error?.toString()}
                                    >
                                        {parameterData.original
                                            .filter((parameter) => {
                                                if (
                                                    parameter.parameterType === ParameterType.SYSTEM_VELOCITY ||
                                                    parameter.parameterType === ParameterType.VELOCITY
                                                ) {
                                                    return false;
                                                }
                                                if (parameter.type === LIST_TYPES.PAN) {
                                                    return shouldShowPanList();
                                                }
                                                return true;
                                            })
                                            .map((parameter) => {
                                                return (
                                                    <option
                                                        key={uniqueId(parameter.fieldName)}
                                                        value={parameter.fieldName}
                                                    >
                                                        <Popover
                                                            id='conclusion-popover'
                                                            showOnHover
                                                            position='bottom-start'
                                                            popperClassName={styles.popOverStyle}
                                                            contentClassName={styles.popOverContentStyle}
                                                            content={
                                                                <table className={styles.popOverTable}>
                                                                    <tbody>
                                                                        <tr>
                                                                            <td>System Name</td>
                                                                            <td>{parameter?.fieldName ?? '_'}</td>
                                                                        </tr>
                                                                        <tr>
                                                                            <td>Display Name</td>
                                                                            <td>{parameter?.message ?? '_'}</td>
                                                                        </tr>
                                                                        <tr>
                                                                            <td>Type</td>
                                                                            <td>{parameter?.type ?? '_'}</td>
                                                                        </tr>
                                                                        <tr>
                                                                            <td>Parameter Type</td>
                                                                            <td>
                                                                                {getFormattedParameterTypeDisplayName(
                                                                                    parameter?.parameterType,
                                                                                    'singular'
                                                                                ) ?? '_'}
                                                                            </td>
                                                                        </tr>
                                                                        <tr>
                                                                            <td>Description</td>
                                                                            <td>{parameter?.description ?? '_'}</td>
                                                                        </tr>
                                                                    </tbody>
                                                                </table>
                                                            }
                                                        >
                                                            <>{parameter.message}</>
                                                        </Popover>
                                                    </option>
                                                );
                                            })}
                                    </Select>
                                );
                            }}
                        </FastField>
                    </div>
                    {listData && (values.actions[index].values as AddToList).fieldName && (
                        <div className={clsx([styles.conclusionColumn, styles.conclusionSelectListContainer])}>
                            <div className={styles.conclusionTo}>to</div>
                            <Field name={`actions[${index}].values.listIds`}>
                                {({ field, form: { setFieldValue, setFieldTouched }, meta }: FieldProps) => {
                                    return (
                                        <Select
                                            name={field.name}
                                            size='sm'
                                            handleClickCallback={(value) => {
                                                setFieldValue(field.name, value);
                                                setFieldTouched(field.name, true);
                                            }}
                                            placeholder={`- ${t('Select List')} -`}
                                            type='listselector'
                                            selectedItems={field.value ? field.value : []}
                                            disabled={isSubmitting}
                                            error={!!(meta.error && meta.touched)}
                                            errorMsg={meta.error?.toString()}
                                        >
                                            {listData
                                                .filter((list) => {
                                                    if (
                                                        parameterData.fieldNameHashmap[
                                                            (values.actions[index].values as AddToList).fieldName
                                                        ].type === LIST_TYPES.PAN
                                                    ) {
                                                        return shouldShowPanList() && list.listType === LIST_TYPES.PAN;
                                                    }
                                                    return list.listType !== LIST_TYPES.PAN;
                                                })
                                                .map(({ name, _formatted }) => {
                                                    return (
                                                        <option key={name} value={_formatted.stringId}>
                                                            {name}
                                                        </option>
                                                    );
                                                })}
                                        </Select>
                                    );
                                }}
                            </Field>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default Conclusion;
